import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { BRAND } from "@/constants/brand";
import { Lock, Mail, LogIn, Loader2 } from "lucide-react";

export default function AdminLogin() {
  const { admin, login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("admin@homecure.co.in");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (admin) navigate(location.state?.from || "/admin", { replace: true });
  }, [admin, navigate, location.state]);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await login(email, password);
      toast.success("Welcome back!");
      navigate("/admin", { replace: true });
    } catch (err) {
      const msg = err?.response?.data?.detail || "Login failed";
      toast.error(typeof msg === "string" ? msg : "Login failed");
    } finally { setSubmitting(false); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-[#E8F0FB] via-[#C3D9F6] to-[#3780E0] p-4" data-testid="admin-login-page">
      <div className="w-full max-w-md">
        <div className="text-center mb-6">
          <img src={BRAND.logoUrl} alt="HomeCure" className="w-14 h-14 object-contain mx-auto bg-white rounded-xl p-1.5 shadow" />
          <div className="mt-3 text-white font-extrabold text-2xl drop-shadow">HomeCure Admin</div>
          <div className="text-white/90 text-sm">{BRAND.parent}</div>
        </div>
        <form onSubmit={submit} className="bg-white rounded-2xl shadow-2xl p-6 md:p-8" data-testid="admin-login-form">
          <h1 className="text-xl font-bold text-[#0F2A4A]">Sign in</h1>
          <p className="text-sm text-slate-500">Use your administrator credentials.</p>
          <div className="mt-5">
            <label className="hc-label">Email</label>
            <div className="relative">
              <Mail className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input required type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="hc-input pl-9" placeholder="admin@homecure.co.in" data-testid="admin-email" />
            </div>
          </div>
          <div className="mt-4">
            <label className="hc-label">Password</label>
            <div className="relative">
              <Lock className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input required type="password" value={password} onChange={(e) => setPassword(e.target.value)} className="hc-input pl-9" placeholder="••••••••" data-testid="admin-password" />
            </div>
          </div>
          <button type="submit" disabled={submitting} className="hc-btn-primary w-full justify-center mt-6 disabled:opacity-60" data-testid="admin-login-submit">
            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <LogIn className="w-4 h-4" />} {submitting ? "Signing in…" : "Sign in"}
          </button>
          <div className="mt-5 text-xs text-slate-500 text-center">
            Default credentials are pre-seeded for first-time setup. <strong>Please change them after login.</strong>
          </div>
        </form>
      </div>
    </div>
  );
}
